import { IconAdd, IconChannelSelective, IconTrash } from '@/icons';
import { Button } from '@/ui/Button/Button';
import Input from '@/ui/Input/Input';
import React from 'react';

import { Flex } from '@/ui/Flex/Flex';
import { T } from '@/ui/Text/Text';
import { DURATIONS, TOASTTYPE, useToast } from '@/ui/Toast/Toast';
import { createId } from '@paralleldrive/cuid2';
import { useIntl } from 'react-intl';
import { GeneralSettingsType } from '../../SettingsSidebar/types';
import { StyledGridContainer } from '../styles';

interface Props {
  buzzers: GeneralSettingsType['buzzers'];
  setBuzzers: (newValue: GeneralSettingsType['buzzers']) => void;
  moreBuzzersAllowed: boolean;
}

function BuzzerHardwareSettings({
  buzzers,
  setBuzzers,
  moreBuzzersAllowed,
}: Props) {
  const intl = useIntl();
  const toast = useToast();

  // --- Utility Functions ---

  const updateBuzzer = (
    index: number,
    key: 'channel' | 'name',
    value: string | null,
  ) => {
    const newbuzzers = [...buzzers];
    newbuzzers[index] = {
      ...newbuzzers[index],
      [key]: value,
    };
    setBuzzers(newbuzzers);
  };

  const deleteBuzzer = (index: number) => {
    const newbuzzers = buzzers.filter((_, i) => i !== index);
    setBuzzers(newbuzzers);
  };

  const addBuzzer = () => {
    if (!moreBuzzersAllowed) {
      toast({
        mode: TOASTTYPE.Danger,
        title: intl.formatMessage({
          defaultMessage: 'No available Buzzers',
          id: 'kjRf8S',
        }),
        subtitle: intl.formatMessage({
          defaultMessage:
            'To add more Buzzers, please register additional controllers.',
          id: '1yKSu8',
        }),
        time: DURATIONS.Medium,
      });
      return;
    }
    setBuzzers([
      ...buzzers,
      {
        id: createId(),
        channel: null,
        name: null,
      },
    ]);
  };

  // --- JSX ---

  return (
    <Flex direction="column" gap={16}>
      <T h4>
        {intl.formatMessage({
          defaultMessage: 'Registered Buzzers',
          id: 'ym2YxP',
        })}
      </T>

      {buzzers.length !== 0 && (
        <StyledGridContainer columns={2}>
          <>
            <T body1>
              {intl.formatMessage({
                defaultMessage: 'Channel',
                id: 'KeO51o',
              })}
            </T>
            <T body1>
              {intl.formatMessage({ defaultMessage: 'Name', id: 'HAlOn1' })}
            </T>
            <div />
          </>

          {buzzers.map((buzzer, index) => {
            const hasAllValues = Boolean(buzzer.channel && buzzer.name);
            return (
              <React.Fragment key={buzzer.id}>
                <div
                  style={{
                    marginTop: hasAllValues ? 0 : '8px',
                  }}
                >
                  <Input
                    required
                    size="small"
                    value={buzzer.channel || ''}
                    onChange={(e) => updateBuzzer(index, 'channel', String(e))}
                    placeholder={intl.formatMessage({
                      defaultMessage: 'Channel',
                      id: 'KeO51o',
                    })}
                    icon={<IconChannelSelective />}
                  />
                </div>

                <div
                  style={{
                    marginTop: hasAllValues ? 0 : '8px',
                  }}
                >
                  <Input
                    required
                    size="small"
                    value={buzzer.name || ''}
                    onChange={(e) => updateBuzzer(index, 'name', String(e))}
                    placeholder={intl.formatMessage({
                      defaultMessage: 'Name',
                      id: 'HAlOn1',
                    })}
                  />
                </div>
                <Button
                  size="minimum"
                  mode="transparent"
                  onClick={() => deleteBuzzer(index)}
                >
                  <IconTrash />
                </Button>
              </React.Fragment>
            );
          })}
        </StyledGridContainer>
      )}

      <Button mode="transparent" onClick={addBuzzer}>
        <IconAdd />
      </Button>
    </Flex>
  );
}

export default BuzzerHardwareSettings;
